import { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import okReservaLogo from '../../assets/okreserva-logo.png';
import { cn } from '../../lib/utils';
import {
  LayoutGrid,
  Building2,
  Users,
  CreditCard,
  BarChart3,
  FileText,
  LogOut,
  Shield,
  ChevronLeft,
  ChevronRight,
  Puzzle,
  Key,
  Link2,
  Brain,
  Bot,
  UserCircle,
  MonitorSmartphone,
  Boxes,
  ChevronDown,
  Globe,
  Layers,
  Wallet,
  Phone,
  Mail,
  MessageCircle
} from 'lucide-react';

const NAV_SECTIONS = [
  {
    label: 'Principal',
    items: [
      { key: 'dashboard', path: '/admin/dashboard', icon: LayoutGrid, label: 'Dashboard' },
      { key: 'restaurants', path: '/admin/restaurants', icon: Building2, label: 'Restaurantes' },
      { key: 'users', path: '/admin/users', icon: Users, label: 'Usuarios' },
      { key: 'clientes', path: '/admin/clientes', icon: UserCircle, label: 'Clientes' },
    ]
  },
  {
    label: 'Operaciones',
    items: [
      { key: 'payments', path: '/admin/payments', icon: CreditCard, label: 'Pagos' },
      { key: 'stripe', path: '/admin/stripe', icon: Layers, label: 'Stripe & Planes' },
      { key: 'ok-wallet', path: '/admin/ok-wallet', icon: Wallet, label: 'OkWallet' },
      { key: 'analytics', path: '/admin/analytics', icon: BarChart3, label: 'Analíticas' },
    ]
  },
  {
    label: 'Desarrollo',
    items: [
      { key: 'widgets', path: '/admin/widgets', icon: MonitorSmartphone, label: 'Widgets' },
      { key: 'api-docs', path: '/admin/api-docs', icon: FileText, label: 'API Docs' },
      { key: 'bubble', path: '/admin/bubble', icon: Boxes, label: 'Bubble.io' },
      { key: 'oauth-sso', path: '/admin/oauth-sso', icon: Key, label: 'OAuth SSO' },
      { key: 'super-connect', path: '/admin/super-connect', icon: Link2, label: 'Super Connect' },
    ]
  },
  {
    label: 'Inteligencia',
    items: [
      { key: 'openrouter', path: '/admin/openrouter', icon: Brain, label: 'OpenRouter' },
      { key: 'ai-agents', path: '/admin/ai-agents', icon: Bot, label: 'Agentes IA' },
      { key: 'retell', path: '/admin/retell', icon: Phone, label: 'Agente Retell' },
      { key: 'email-server', path: '/admin/email-server', icon: Mail, label: 'Email Server' },
      { key: 'kapso', path: '/admin/kapso', icon: MessageCircle, label: 'Kapso WhatsApp' },
      { key: 'mcp', path: '/admin/mcp', icon: Puzzle, label: 'MCP Server' },
      { key: 'rapidapi', path: '/admin/rapidapi', icon: Globe, label: 'RapidAPI Hub' },
    ]
  },
  {
    label: 'Cumplimiento',
    items: [
      { key: 'legal', path: '/admin/legal', icon: Shield, label: 'Legal' },
    ]
  },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const user = localStorage.getItem('admin_user');
    if (!token) { navigate('/admin'); return; }
    if (user) {
      try { setAdminUser(JSON.parse(user)); } catch { localStorage.removeItem('admin_user'); }
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex min-h-screen bg-[#f4f6fa] admin-light">

      {/* ── Sidebar ── */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-40 flex flex-col bg-[#0f1117] transition-all duration-300 ease-in-out",
        collapsed ? "w-[68px]" : "w-[240px]"
      )}>

        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-white/5">
          {!collapsed && (
            <Link to="/admin/dashboard" className="flex items-center gap-2.5 min-w-0">
              <img
                src={okReservaLogo}
                alt="OkReserva"
                className="h-7 w-auto flex-shrink-0"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
              <p className="text-[10px] text-orange-400/80 font-medium uppercase tracking-widest whitespace-nowrap">Super Admin</p>
            </Link>
          )}
          {collapsed && (
            <Link to="/admin/dashboard" className="mx-auto">
              <img
                src={okReservaLogo}
                alt="OkReserva"
                className="h-7 w-7 object-contain"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </Link>
          )}
          {!collapsed && (
            <button onClick={() => setCollapsed(true)} className="p-1.5 rounded-md text-white/30 hover:text-white/70 hover:bg-white/5 transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2 scrollbar-hide">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label} className="mb-5">
              {!collapsed && (
                <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/25 select-none">
                  {section.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const active = isActive(item.path);
                  const Icon = item.icon;
                  return (
                    <li key={item.key}>
                      <Link
                        to={item.path}
                        title={collapsed ? item.label : undefined}
                        className={cn(
                          "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150",
                          active
                            ? "bg-orange-500/15 text-orange-400 border border-orange-500/20"
                            : "text-white/50 hover:bg-white/5 hover:text-white/80 border border-transparent",
                          collapsed && "justify-center px-2"
                        )}
                        data-testid={`admin-nav-${item.key}`}
                      >
                        <Icon className={cn(
                          "flex-shrink-0 transition-colors",
                          collapsed ? "h-5 w-5" : "h-4 w-4",
                          active ? "text-orange-400" : "text-white/40 group-hover:text-white/60"
                        )} />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                        {active && !collapsed && (
                          <span className="ml-auto h-1.5 w-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Collapse button (when collapsed) */}
        {collapsed && (
          <div className="flex justify-center py-2 border-t border-white/5">
            <button onClick={() => setCollapsed(false)} className="p-2 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* User Footer */}
        <div className={cn(
          "border-t border-white/5 p-3",
          collapsed ? "flex flex-col items-center gap-2" : "space-y-2"
        )}>
          {!collapsed && adminUser && (
            <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/5">
              <p className="text-xs font-medium text-white/70 truncate">{adminUser.name || 'Super Admin'}</p>
              <p className="text-[10px] text-white/30 truncate">{adminUser.email}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            title="Cerrar sesión"
            className={cn(
              "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-all duration-150 border border-transparent hover:border-red-500/20",
              collapsed ? "w-full justify-center" : "w-full"
            )}
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className={cn(
        "flex-1 min-h-screen transition-all duration-300 ease-in-out",
        collapsed ? "ml-[68px]" : "ml-[240px]"
      )}>
        <Outlet />
      </main>
    </div>
  );
}
